import { useBottomSheetInternal } from "@gorhom/bottom-sheet";
import { useCallback, useEffect, useRef, type RefObject } from "react";
import {
  Platform,
  TextInput,
  type BlurEvent,
  type FocusEvent,
} from "react-native";

// Gorhom 5.x types `textInputNodesRef` as `Set<number>` because its own
// BottomSheetTextInput stores values from `findNodeHandle(ref.current)`.
// `textInputNodesRef` is a plain JS-thread React ref — it's not a reanimated
// shared value — so we can safely store host-instance refs and compare with
// `===`, no findNodeHandle required. We cast once here; drop the cast when
// gorhom's types accept `HostInstance`.
//
// `animatedKeyboardState` is different: gorhom reads `state.value.target`
// from a reanimated worklet, so it MUST remain a primitive (number) or
// Reanimated will throw "cannot be sent to the UI runtime". We get that
// number for free from `e.nativeEvent.target` on focus events, so we don't
// need findNodeHandle here either.
type AnyRefSet = Set<unknown>;

export function useBottomSheetInputIntegration(
  ref: RefObject<TextInput | null>,
  onFocus?: (e: FocusEvent) => void,
  onBlur?: (e: BlurEvent) => void,
) {
  const internal = useBottomSheetInternal(true);
  // Caches the native tag seen on the last focus event so the unmount
  // cleanup can clear `state.target` if it still points at this input.
  const nativeTagRef = useRef<number | undefined>(undefined);

  const handleFocus = useCallback(
    (e: FocusEvent) => {
      if (internal) {
        nativeTagRef.current = e.nativeEvent.target;
        internal.animatedKeyboardState.set((state) => ({
          ...state,
          target: e.nativeEvent.target,
        }));
      }
      onFocus?.(e);
    },
    [internal, onFocus],
  );

  const handleBlur = useCallback(
    (e: BlurEvent) => {
      if (internal && Platform.OS !== "web") {
        const { animatedKeyboardState, textInputNodesRef } = internal;
        const nodes = textInputNodesRef.current as AnyRefSet;
        const focused = TextInput.State.currentlyFocusedInput();
        const state = animatedKeyboardState.get();
        const movedToSibling = focused != null && nodes.has(focused);
        if (state.target === e.nativeEvent.target && !movedToSibling) {
          animatedKeyboardState.set((s) => ({ ...s, target: undefined }));
        }
      }
      onBlur?.(e);
    },
    [internal, onBlur],
  );

  useEffect(() => {
    if (!internal || Platform.OS === "web") return;
    const node = ref.current;
    if (!node) return;
    const { textInputNodesRef, animatedKeyboardState } = internal;
    const nodes = textInputNodesRef.current as AnyRefSet;
    nodes.add(node);
    return () => {
      nodes.delete(node);
      const tag = nativeTagRef.current;
      if (tag == null) return;
      const state = animatedKeyboardState.get();
      if (state.target === tag) {
        animatedKeyboardState.set((s) => ({ ...s, target: undefined }));
      }
    };
  }, [internal, ref]);

  return { handleFocus, handleBlur };
}
